// FILE: src/pages/PostPayment.jsx
// Handles BOTH flows:
// 1) Email verification (mode=verifyEmail & oobCode)
// 2) Stripe finish (session_id) -> verify on backend -> redirect to /login (NO auto-login)
import React, { useEffect, useRef, useState } from "react";
import { auth } from "../firebase";
import { signOut, applyActionCode } from "firebase/auth";
import { useNavigate } from "react-router-dom";

function safeParseJSON(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// Last-resort CF base; no credentials
const FN_BASE =
  (typeof window !== "undefined" && window.__FN_BASE__) ||
  process.env.REACT_APP_FUNCTIONS_BASE_URL ||
  "https://us-central1-lastwishbox-business.cloudfunctions.net";

function readSavedPw(sessionId) {
  try {
    const now = Date.now();
    const tsStr =
      sessionStorage.getItem("lwb_reg_pw_ts") ||
      localStorage.getItem("lwb_reg_pw_ts") ||
      "";
    const ts = Number(tsStr);
    // expire saved pw after 1 hour for safety
    if (ts && now - ts > 60 * 60 * 1000) return "";

    return (
      sessionStorage.getItem("lwb_reg_pw") ||
      sessionStorage.getItem(`pw:${sessionId}`) ||
      sessionStorage.getItem("pw:pending") ||
      localStorage.getItem("lwb_reg_pw") ||
      localStorage.getItem(`pw:${sessionId}`) ||
      localStorage.getItem("pw:pending") ||
      ""
    );
  } catch {
    return "";
  }
}

function clearPwKeys(sessionId) {
  try {
    sessionStorage.removeItem(`pw:${sessionId}`);
    sessionStorage.removeItem("pw:pending");
    sessionStorage.removeItem("lwb_reg_pw");
    sessionStorage.removeItem("lwb_reg_pw_ts");
  } catch {}
  try {
    localStorage.removeItem(`pw:${sessionId}`);
    localStorage.removeItem("pw:pending");
    localStorage.removeItem("lwb_reg_pw");
    localStorage.removeItem("lwb_reg_pw_ts");
  } catch {}
}

function BrandedShell({ title, subtitle, children }) {
  return (
    <div style={{
      minHeight: "100vh", background: "#f6f7fb",
      display: "flex", alignItems: "center", justifyContent: "center", padding: "20px"
    }}>
      <div style={{
        width: "100%", maxWidth: 480, background: "#fff", borderRadius: 16,
        boxShadow: "0 10px 40px rgba(42,5,22,0.10)", padding: "36px 32px", textAlign: "center"
      }}>
        <img src="/Lastwishbox-logo.png" alt="LastWish Box" style={{ height: 48, marginBottom: 20 }} />
        {title && <h2 style={{ margin: "0 0 8px", color: "#2a0516", fontSize: 22, fontWeight: 800 }}>{title}</h2>}
        {subtitle && <p style={{ margin: "0 0 20px", color: "#657899", fontSize: 15 }}>{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "16px 0" }}>
      <div style={{
        width: 40, height: 40, borderRadius: "50%",
        border: "3px solid #f0e8ec", borderTopColor: "#2a0516",
        animation: "lwb-spin 0.8s linear infinite"
      }} />
      <style>{"@keyframes lwb-spin { to { transform: rotate(360deg); } }"}</style>
    </div>
  );
}

export default function PostPayment() {
  const nav = useNavigate();
  const [stage, setStage] = useState("working"); // working | needPw | verifying | verified | fail
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const sidRef = useRef("");

  async function tryPost(url, body) {
    const r = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "omit",
    });
    const t = await r.text();
    const d = safeParseJSON(t) || {};
    return { ok: r.ok, status: r.status, text: t, data: d };
  }


  async function completeExchange(sessionId, passwordOrEmpty) {
    const body = {
      session_id: sessionId,
      ...(passwordOrEmpty ? { password: passwordOrEmpty } : {}),
    };

    const attempts = [];

    // Primary route: unified business app V2 endpoint only
    attempts.push(async () => {
      const res = await tryPost("/api/verifyCheckoutSessionV2", body);
      if (!res.ok) throw new Error(res.data?.error || res.text || `HTTP ${res.status}`);
      return { via: "/api/verifyCheckoutSessionV2", res };
    });

    // Direct CF fallback: same V2 endpoint in the business project
    attempts.push(async () => {
      const res = await tryPost(`${FN_BASE}/verifyCheckoutSessionV2`, body);
      if (!res.ok) throw new Error(res.data?.error || res.text || `HTTP ${res.status}`);
      return { via: "direct CF URL /verifyCheckoutSessionV2", res };
    });

    let lastErr = null;
    for (const fn of attempts) {
      try {
        await fn();
        return true;
      } catch (e) {
        lastErr = e;

      }
    }
    throw lastErr || new Error("Could not complete registration.");
  }

  useEffect(() => {
    const usp = new URLSearchParams(window.location.search);
    const mode = usp.get("mode");
    const oobCode = usp.get("oobCode");

    // ---------- A0) Password reset ----------
    if (mode === "resetPassword") {
      nav(`/reset-password?mode=resetPassword&oobCode=${oobCode || ""}&continueUrl=${encodeURIComponent(window.location.href)}`, { replace: true });
      return;
    }

    // ---------- A) Email verification ----------
    if (mode === "verifyEmail") {
      if (!oobCode) {
        setError("Missing verification code.");
        setStage("fail");
        return;
      }
      (async () => {
        try {
          setStage("verifying");
          try {
            await applyActionCode(auth, oobCode);
          } catch (e) {
            // If Firebase already consumed the code before redirecting, treat invalid-action-code as success.
            if (e?.code === "auth/invalid-action-code") {
              setStage("verified");
              setTimeout(() => nav("/login?verified=1", { replace: true }), 600);
              return;
            }
            throw e;
          }
          setStage("verified");
          setTimeout(() => nav("/login?verified=1", { replace: true }), 600);
        } catch (e) {
          setError(e?.message || "Verification link invalid or expired.");
          setStage("fail");
        }
      })();
      return;
    }

    // ---------- B) Stripe finish ----------
    const _sid = usp.get("session_id") || usp.get("sid") || usp.get("sessionId");
    if (!_sid) {
      setStage("verified");
      setTimeout(() => nav("/login?verified=1", { replace: true }), 400);
      return;
    }
    sidRef.current = _sid;

    // Critical: never carry auth session into app after checkout
    (async () => {
      try {
        await signOut(auth);
      } catch {}
    })();

    const savedPw = readSavedPw(_sid);

    (async () => {
      try {
        // If we have no password stored, prompt for it. (Otherwise login will fail later.)
        if (!savedPw) {
          setStage("needPw");
          return;
        }

        setStage("working");
        await completeExchange(_sid, savedPw);

        // cleanup
        clearPwKeys(_sid);
        try {
          await signOut(auth);
        } catch {}

        nav("/login?created=1", { replace: true });
      } catch (e) {
        setError(e?.message || "Could not complete registration.");
        setStage("fail");
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav]);

  async function onSetPassword(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");

    try {
      const sessionId = sidRef.current;
      if (!sessionId) throw new Error("Missing session id.");

      const trimmed = (pw || "").trim();
      if (trimmed.length < 8) throw new Error("Password must be at least 8 characters.");

      await completeExchange(sessionId, trimmed);

      clearPwKeys(sessionId);
      try {
        await signOut(auth);
      } catch {}

      nav("/login?created=1", { replace: true });
    } catch (e2) {
      setError(e2?.message || "Failed to set password.");
      setBusy(false);
    }
  }

  if (stage === "verifying")
    return <BrandedShell title="Verifying your email" subtitle="Please wait a moment…"><Spinner /></BrandedShell>;
  if (stage === "verified")
    return <BrandedShell title="Email verified!" subtitle="Redirecting you now…"><Spinner /></BrandedShell>;
  if (stage === "working")
    return <BrandedShell title="Setting up your account" subtitle="Your payment was successful. We’re finishing your registration…"><Spinner /></BrandedShell>;

  if (stage === "needPw") {
    return (
      <BrandedShell title="Create your password" subtitle="Your payment was successful. Set a password to access your account.">
        <form onSubmit={onSetPassword} style={{ display: "grid", gap: 12 }}>
          <input
            type="password"
            placeholder="New password (min 8 chars)"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            style={{
              width: "100%", height: 44, padding: "0 12px", borderRadius: 10,
              border: "1px solid #d6d6e7", fontSize: 15, boxSizing: "border-box"
            }}
            autoComplete="new-password"
          />
          <button
            type="submit"
            disabled={busy}
            style={{
              height: 44, borderRadius: 10, background: "#2a0516", color: "#fff",
              border: "none", fontWeight: 800, fontSize: 15, cursor: busy ? "not-allowed" : "pointer"
            }}
          >
            {busy ? "Saving…" : "Continue →"}
          </button>
          {error && <div style={{ color: "#a00", fontSize: 14 }}>{error}</div>}
        </form>
      </BrandedShell>
    );
  }

  return (
    <BrandedShell title="Something went wrong" subtitle="We couldn’t finish your registration.">
      <div style={{
        background: "#fee2e2", color: "#7a0b0b", borderRadius: 8,
        padding: "10px 14px", marginBottom: 16, fontSize: 14
      }}>
        {error || "Unknown error"}
      </div>
      <p style={{ color: "#657899", fontSize: 14, margin: "0 0 16px" }}>
        Please refresh and try again, or contact support if the problem persists.
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          height: 44, borderRadius: 10, background: "#2a0516", color: "#fff",
          border: "none", fontWeight: 800, fontSize: 15, cursor: "pointer", width: "100%"
        }}
      >
        Try again
      </button>
    </BrandedShell>
  );
}
